import { Row } from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { notificationRuleBaseSchema } from '@/features/notification-rules/schemas/notification-rules.schema.ts';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { NotificationRulesTableRowActionEditDialog } from '@/features/notification-rules/components/notification-rules-table/notification-rules-table-row-action-edit-dialog.tsx';
import NotificationRulesTableRowActionDeleteDialog from '@/features/notification-rules/components/notification-rules-table/notification-rules-table-row-action-delete-dialog.tsx';
import { NotificationRulesTableRowActionDuplicateDialog } from '@/features/notification-rules/components/notification-rules-table/notification-rules-table-row-action-duplicate-dialog.tsx';
import { NotificationRulesTableRowActionUnsubscribeDialog } from '@/features/notification-rules/components/notification-rules-table/notification-rules-table-row-action-unsubscribe-dialog.tsx';
import { RuleAssignmentManagementDialog } from '@/features/notification-rules/components/notification-rules-table/rule-assignment/rule-assignment-management.tsx';
import { hasPermission } from '@/features/users/schemas/users.permissions.ts';
import { useSession } from '@/services/providers/use-session.ts';
import { CredenzaTrigger } from '@/components/ui/credenza.tsx';

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
}

export function NotificationRulesTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const notificationRule = notificationRuleBaseSchema.parse(row.original);
  const { userId, role } = useSession();
  const isAdmin = hasPermission(
    { userId, role },
    'notification-rules',
    'update.access-management'
  );
  const isCompanyManaged = notificationRule.managedBy === 'company-managed';

  return (
    <div className="flex justify-end">
      {isAdmin && isCompanyManaged ? (
        <RuleAssignmentManagementDialog
          ruleId={notificationRule.notificationRuleId}
          plantId={notificationRule.plantId}
          ruleName={notificationRule.notificationTitle}
          plantName={notificationRule.plantName}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="data-[state=open]:bg-muted flex h-8 w-8 p-0 hover:bg-gray-200"
              >
                <DotsHorizontalIcon className="h-5 w-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuItem asChild>
                <NotificationRulesTableRowActionEditDialog
                  notificationRule={notificationRule}
                />
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <NotificationRulesTableRowActionDuplicateDialog
                  notificationRule={notificationRule}
                />
              </DropdownMenuItem>
              <CredenzaTrigger asChild>
                <DropdownMenuItem>Assignments</DropdownMenuItem>
              </CredenzaTrigger>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <NotificationRulesTableRowActionDeleteDialog
                  notificationRule={notificationRule}
                />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </RuleAssignmentManagementDialog>
      ) : !isAdmin && isCompanyManaged ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="data-[state=open]:bg-muted flex h-8 w-8 p-0 hover:bg-gray-200"
            >
              <DotsHorizontalIcon className="h-5 w-5" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]">
            <DropdownMenuItem asChild>
              <NotificationRulesTableRowActionUnsubscribeDialog
                notificationRule={notificationRule}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="data-[state=open]:bg-muted flex h-8 w-8 p-0 hover:bg-gray-200"
            >
              <DotsHorizontalIcon className="h-5 w-5" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]">
            <DropdownMenuItem asChild>
              <NotificationRulesTableRowActionEditDialog notificationRule={notificationRule} />
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <NotificationRulesTableRowActionDeleteDialog
                notificationRule={notificationRule}
              />
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <NotificationRulesTableRowActionDuplicateDialog
                notificationRule={notificationRule}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
